import { gql } from 'graphql-request'
import { ColorEnum } from 'app/utils/mapColors'

export type Collectible = {
  id: string
  title: string
  quarter: string
  image: string
  href: string
  hasContent?: boolean
  availableFrom?: string | null
  availableUntil?: string | null
  backgroundColour?: ColorEnum
  year?: number | null
  isPurchasable?: boolean
}

export type CollectiblesQueryVariables = {
  where?: Record<string, any>
  sort?: string
  limit?: number
}

export const createCollectiblesQuery = () => gql`
  query getCollectibles(
    $where: Collectible_where
    $sort: String
    $limit: Int
  ) {
    Collectibles(where: $where, sort: $sort, limit: $limit) {
      docs {
        id
        title
        quarter
        hasContent
        year
        isPurchasable
        availableFrom
        availableUntil
        backgroundColour
        slug
        thumbnail {
          url
          sizes {
            square {
              url
            }
          }
        }
      }
    }
  }
`

export const buildYearCollectiblesVariables = (
  year: number
): CollectiblesQueryVariables => ({
  where: { year: { equals: year } },
  sort: 'availableFrom',
  limit: 100,
})

export const buildRoadmapCollectiblesVariables = (
  roadmapId?: string | null,
  fallbackYear?: number
): CollectiblesQueryVariables => {
  if (roadmapId) {
    return {
      where: { roadmap: { equals: roadmapId } },
      sort: 'availableFrom',
      limit: 100,
    }
  }

  if (typeof fallbackYear === 'number') {
    return buildYearCollectiblesVariables(fallbackYear)
  }

  return {
    sort: 'availableFrom',
    limit: 100,
  }
}

export const buildPurchasableCollectiblesVariables =
  (): CollectiblesQueryVariables => ({
    where: { isPurchasable: { equals: true } },
    sort: '-availableFrom',
    limit: 100,
  })

const parseQuarter = (quarter: string): number | null => {
  const match = quarter.toUpperCase().match(/Q([1-4])/)
  if (!match) return null
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : null
}

const parseDate = (value?: string | null): number | null => {
  if (!value) return null
  const parsed = new Date(value)
  const time = parsed.getTime()
  return Number.isNaN(time) ? null : time
}

export const sortCollectiblesByQuarterOrder = (
  items: Collectible[]
): Collectible[] =>
  [...items].sort((a, b) => {
    const quarterA = parseQuarter(a.quarter || '')
    const quarterB = parseQuarter(b.quarter || '')

    if (quarterA !== null && quarterB !== null && quarterA !== quarterB) {
      return quarterA - quarterB
    }

    const availableFromA = parseDate(a.availableFrom)
    const availableFromB = parseDate(b.availableFrom)
    if (availableFromA !== null && availableFromB !== null) {
      if (availableFromA !== availableFromB) {
        return availableFromA - availableFromB
      }
    } else if (availableFromA !== null || availableFromB !== null) {
      return availableFromA !== null ? -1 : 1
    }

    return (a.title || '').localeCompare(b.title || '')
  })

export const mapToCollectibles = (data: any): Collectible[] => {
  return (
    data?.Collectibles?.docs?.map((item: any) => ({
      id: item.id,
      title: item.title,
      image: item.thumbnail?.sizes?.square?.url || item.thumbnail?.url || '',
      quarter: item.quarter || '',
      href: item.slug ? `/collectible/${item.slug}` : '',
      hasContent: item.hasContent,
      availableFrom: item.availableFrom,
      availableUntil: item.availableUntil,
      backgroundColour: item.backgroundColour,
      year: item.year,
      isPurchasable: item.isPurchasable,
    })) || []
  )
}
